import aiGame from '@/services/aiGame';
import pvp from '@/services/pvp';
import KOMI from '@/config/komi.js';
import Vue from 'vue';
import {LADDER_TYPES} from '@/constant/ladder';
import {GAME_MODES} from '@/constant/chessboard';

const state = {
  gameStatistics: {},
  aiLevel: 1,
  sgf: null,
  initialSgf: null,
  userColor: 'black',
  boardSize: 9,
  capCount: {black: 0, white: 0},
  opponentName: null,
  createdTime: null,
  overGameCreatedTime: null,
  hasUnfinishedGame: false,
  kifu: null,
  kifus: null,
  komi: KOMI.DEFAULT,
  pvpLastKey: null,
  pveLastKey: null,
  style: '',
  kifusAmount: 20,
  kifusType: [
    'practice',
    'challenge',
    'study',
    'pvp',
    'verification',
    'hjjCourse',
    'ladder',
  ],
  kifuReviewGame: null,
  id: null,
  aiId: null,
  supportCount: 0,
  coursePlan: null,
  byoYomiTime: null,
  byoYomiAmount: null,
  blackByoYomiAmount: null,
  blackByoYomiTime: null,
  blackTime: null,
  whiteByoYomiAmount: null,
  whiteByoYomiTime: null,
  whiteTime: null,
  stage: null,
  level: null,
  moveOptions: null,
  fusekiMaxStep: null,
  fusekiPassScore: null,
  lastMoveAt: null,
};

const getters = {
  hasUnfinishedGame(state) {
    return state.hasUnfinishedGame;
  },
  kifu(state) {
    return state.kifu;
  },
  blackTime(state) {
    return {
      mainTime: state.blackTime,
      byoYomiTime: state.blackByoYomiTime,
      byoYomiAmount: state.blackByoYomiAmount,
    };
  },
  whiteTime(state) {
    return {
      mainTime: state.whiteTime,
      byoYomiTime: state.whiteByoYomiTime,
      byoYomiAmount: state.whiteByoYomiAmount,
    };
  },

  getOpponentAvatar(state) {
    return ({isLadderMode}) => {
      const isUserBlack = state.userColor === 'black';
      if (isLadderMode) {
        return isUserBlack ? 'ladder_w' : 'ladder_b';
      }
      let opponentAvatar = isUserBlack ? 'ai_w' : 'ai_b';
      const {aiId = 1, opponentName, createdTime} = state || {};
      if (
        createdTime &&
        opponentName !== '黑小嘉' &&
        opponentName !== '白小嘉'
      ) {
        const isKid =
          state.coursePlan.includes('kid') || state.coursePlan.includes('kgp');
        if (isKid) {
          opponentAvatar = `avatar_tsumego_ai_${aiId}-kid`;
        } else {
          opponentAvatar = `avatar_tsumego_ai_${aiId}`;
        }
      }
      return opponentAvatar;
    };
  },
};

const actions = {
  async getKifus({commit}, username) {
    const result = await aiGame.getKifus(
      state.kifusAmount,
      username,
      state.kifusType
    );

    const kifus = result.data.kifuList;
    let pveLastKey = state.pveLastKey;
    let pvpLastKey = state.pvpLastKey;
    if (kifus.length !== 0) {
      if (result.data.pveLastKey) {
        pveLastKey = result.data.pveLastKey;
      }
      if (result.data.pvpLastKey) {
        pvpLastKey = result.data.pvpLastKey;
      }
    }

    commit('setKifus', {
      kifus,
      pveLastKey,
      pvpLastKey,
      first: true,
    });
  },
  createPracticeGame(
    {commit},
    {userColor, aiLevel, boardSize, style, opponentName, coursePlan, aiId}
  ) {
    return aiGame
      .createPracticeGame(
        userColor,
        aiLevel,
        boardSize,
        style,
        opponentName,
        coursePlan,
        aiId
      )
      .then((result) => {
        if (style) commit('setStyle', style);
        result.coursePlan = coursePlan;
        commit('setGameData', result);
        commit('setHasUnfinishedGame', false);
        return result;
      });
  },
  createCourseGame({commit}, {courseUniqueKey, coursePlan}) {
    return aiGame
      .createCourseGame(courseUniqueKey, coursePlan)
      .then((result) => {
        commit('setGameData', result);
        return result;
      });
  },
  createVerificationGame({commit}, gameData) {
    return aiGame
      .createVerificationGame(
        gameData.rank,
        gameData.verificationId,
        gameData.gameId,
        gameData.coursePlan
      )
      .then((result) => {
        commit('setGameData', result);
        return result;
      });
  },
  async createLadderGame({commit, dispatch}, {stage}) {
    const result = await aiGame.createLadderGame({level: stage});
    commit('setGameData', result);
    dispatch('ladder/setNextStage', {type: LADDER_TYPES.GAME}, {root: true});
    return result;
  },
  async createLadderEndgame({commit, dispatch}, {stage}) {
    const result = await aiGame.createLadderEndgame({level: stage});
    commit('setGameData', result);
    dispatch('ladder/setNextStage', {type: LADDER_TYPES.ENDGAME}, {root: true});
    return result;
  },
  async createLadderFuseki({commit, dispatch}, {stage}) {
    const result = await aiGame.createLadderFuseki({level: stage});
    commit('setGameData', result);
    dispatch('ladder/setNextStage', {type: LADDER_TYPES.FUSEKI}, {root: true});
    const info = await dispatch(
      'ladder/getStageInfo',
      {
        type: LADDER_TYPES.FUSEKI,
        stage,
      },
      {root: true}
    );
    commit('setFusekiInfo', info);
    return result;
  },
  async getUnfinishedGame({commit, dispatch}, gameMode = 'practice') {
    const isLeaderList = [
      GAME_MODES.LADDER,
      GAME_MODES.LADDER_FUSEKI,
      GAME_MODES.LADDER_ENDGAME,
    ];
    const result = await aiGame.getUnfinishedGame(gameMode);
    if (!result) {
      commit('resetGameData');
      commit('setHasUnfinishedGame', false);
      return result;
    }
    commit('setGameData', result);
    commit('setHasUnfinishedGame', true);
    if (isLeaderList.includes(gameMode)) {
      dispatch('ladder/setNextStage', {type: LADDER_TYPES.GAME}, {root: true});
    }
    if (gameMode === GAME_MODES.LADDER_FUSEKI) {
      const info = await dispatch(
        'ladder/getStageInfo',
        {
          type: LADDER_TYPES.FUSEKI,
          stage: result.level,
        },
        {root: true}
      );
      commit('setFusekiInfo', info);
    }
    return result;
  },
  getUnfinishedCourseGame({commit}, {courseUniqueKey}) {
    return aiGame.getUnfinishedCourseGame(courseUniqueKey).then((result) => {
      if (result) {
        commit('setGameData', result);
        commit('setHasUnfinishedGame', true);
        return result;
      } else {
        commit('resetGameData');
        commit('setHasUnfinishedGame', false);
        return result;
      }
    });
  },
  getUnfinishedVerificationGame({commit}, gameData) {
    return aiGame
      .getUnfinishedVerificationGame(
        gameData.verificationId,
        gameData.gameId,
        gameData.gameCreatedTime
      )
      .then((result) => {
        if (result) {
          commit('setGameData', result);
          commit('setHasUnfinishedGame', true);
          return result;
        } else {
          commit('resetGameData');
          commit('setHasUnfinishedGame', false);
          return result;
        }
      });
  },
  updateGame({state}, {move, isNeedSendTime}) {
    const createdTime = state.createdTime;
    const data = {
      createdTime,
      move,
    };
    if (isNeedSendTime) {
      data.time = state[state.userColor + 'Time'];
      data.byoYomiTime = state[state.userColor + 'ByoYomiTime'];
      data.byoYomiAmount = state[state.userColor + 'ByoYomiAmount'];
    }
    return aiGame.updateGame(data);
  },
  nextMove({state}) {
    return aiGame.nextMove(state.createdTime);
  },
  getJudgement({state}) {
    return aiGame.getJudgement(state.createdTime);
  },
  confirmJudgement({state}) {
    return aiGame.confirmJudgement(state.createdTime);
  },
  resign({state, commit}) {
    return aiGame.resign(state.createdTime).then((result) => {
      commit('setHasUnfinishedGame', false);
      return result;
    });
  },
  getKifu({commit}, {createdTime, gameMode, username}) {
    if (gameMode === 'pvp') {
      return pvp.getPvpKifu(createdTime).then((result) => {
        commit('setKifuData', result);
      });
    } else {
      return aiGame.getKifu(createdTime, username).then((result) => {
        commit('setKifuData', result);
      });
    }
  },
};
const mutations = {
  overGameData(state) {
    state.overGameCreatedTime = state.createdTime;
    state.createdTime = null;
    state.sgf = null;
    state.supportCount = 0;
  },
  setGameData(state, game) {
    if (game) {
      state.initialSgf = game.initialSgf;
      state.sgf = game.sgf;
      state.userColor = game.userColor;
      state.opponentName = game.opponentName;
      state.createdTime = game.createdTime;
      state.aiLevel = game.aiLevel;
      state.boardSize = game.boardSize;
      state.id = game.id;
      state.aiId = game.aiId;
      state.supportCount = game.supportCount;
      state.coursePlan = game.coursePlan;
      state.komi = game.komi;
      state.byoYomiTime = game.byoYomiTime;
      state.byoYomiAmount = game.byoYomiAmount;
      state.blackByoYomiAmount = game.blackByoYomiAmount;
      state.blackByoYomiTime = game.blackByoYomiTime;
      state.blackTime = game.blackTime;
      state.whiteByoYomiAmount = game.whiteByoYomiAmount;
      state.whiteByoYomiTime = game.whiteByoYomiTime;
      state.whiteTime = game.whiteTime;
      state.stage = game.level;
      state.level = game.level;
      state.moveOptions = game.moveOptions;
      state.lastMoveAt = game.lastMoveAt;
    }
  },
  resetGameData(state) {
    state.initialSgf = null;
    state.sgf = null;
    state.userColor = 'black';
    state.opponentName = null;
    state.createdTime = null;
    state.aiLevel = 1;
    state.boardSize = 9;
    state.style = '';
    state.id = null;
    state.aiId = null;
    state.supportCount = 0;
    state.coursePlan = null;
    state.moveOptions = null;
    state.fusekiMaxStep = null;
    state.fusekiPassScore = null;
    state.lastMoveAt = null;
  },
  setFusekiInfo(state, info) {
    state.fusekiMaxStep = info.userStep;
    state.fusekiPassScore = info.passScore;
  },
  setStyle(state, style) {
    state.style = style;
  },
  setUserColor(state, color) {
    state.userColor = color > 0 ? 'black' : 'white'; // 1 or -1;
  },
  setSgf(state, sgf) {
    if (state.createdTime) {
      state.sgf = sgf;
    }
  },
  setCapCount(state, capCount) {
    state.capCount = capCount;
  },
  setBoardSize(state, size) {
    state.boardSize = size;
  },
  setHasUnfinishedGame(state, value) {
    state.hasUnfinishedGame = value;
  },
  setKifuData(state, kifu) {
    state.kifu = kifu;
  },
  resetKifuData(state) {
    state.kifu = null;
  },
  setKifuIsReviewed(state) {
    Vue.set(state.kifu, 'isReviewed', true);
  },
  setKifuReviewGame(state, kifuReviewGame) {
    if (kifuReviewGame?.reports?.pathMoveArray) {
      /**
       * 去除第0手的复盤變化圖
       * 因為在第一手的時候看變化圖，看到的是第0手的盤面狀態下後續該下哪
       * 所以全部往後推一格
       */
      const newKifuReviewGame = structuredClone(kifuReviewGame);
      newKifuReviewGame.reports.pathMoveArray.unshift([]);
      state.kifuReviewGame = newKifuReviewGame;
    } else {
      state.kifuReviewGame = kifuReviewGame;
    }
  },
  setKifus(state, {kifus, pveLastKey, pvpLastKey, first}) {
    if (first) {
      state.kifus = kifus;
    } else {
      state.kifus = [...state.kifus, ...kifus];
    }
    state.pveLastKey = pveLastKey;
    state.pvpLastKey = pvpLastKey;
  },
  setKifuAmount(state, data) {
    state.kifusAmount = data;
  },
  resetByoYomiTime(state, color) {
    if (color === 'black') {
      state.blackByoYomiTime = state.byoYomiTime;
    } else if (color === 'white') {
      state.whiteByoYomiTime = state.byoYomiTime;
    }
  },
  setByoYomiAmount(state, {color, byoYomiAmount}) {
    if (color === 'black') {
      state.blackByoYomiAmount = byoYomiAmount;
    } else if (color === 'white') {
      state.whiteByoYomiAmount = byoYomiAmount;
    }
  },
  setByoYomiTime(state, {color, byoYomiTime}) {
    if (color === 'black') {
      state.blackByoYomiTime = byoYomiTime;
    } else if (color === 'white') {
      state.whiteByoYomiTime = byoYomiTime;
    }
  },
  setTime(state, {color, time}) {
    if (color === 'black') {
      state.blackTime = time;
    } else if (color === 'white') {
      state.whiteTime = time;
    }
  },

  setGameTime(state, game) {
    state.blackTime = game.blackTime ?? state.blackTime;
    state.whiteTime = game.whiteTime ?? state.whiteTime;
    state.blackByoYomiTime = game.blackByoYomiTime ?? state.blackByoYomiTime;
    state.whiteByoYomiTime = game.whiteByoYomiTime ?? state.whiteByoYomiTime;
    state.blackByoYomiAmount =
      game.blackByoYomiAmount ?? state.blackByoYomiAmount;
    state.whiteByoYomiTime = game.whiteByoYomiTime ?? state.whiteByoYomiTime;
    state.lastMoveAt = game.lastMoveAt ?? state.lastMoveAt;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
